import ExcelJS from 'exceljs';
import {
  BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_EXPORT_TITLE,
  BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_FILE_NAME,
  BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_SHEET_NAME
} from './constant';
import { BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_COLUMNS } from './column-mapping';
import type { ExcelExportOptions, ExportFromApiOptions } from './types';

const buildFileName = (base: string) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base}-${timestamp}.xlsx`;
};

const createWorkbook = (sheetName: string) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
};

const applyHeaderStyles = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

const applyDataRowStyles = (row: ExcelJS.Row, bold: boolean) => {
  if (bold) {
    row.eachCell(cell => {
      cell.font = { ...(cell.font || {}), bold: true };
    });
  }
};

export const exportReportExcel = async (
  rows: BanHangRecord[] = [],
  options?: Partial<ExcelExportOptions<BanHangRecord>>
) => {
  const columns = options?.columns || BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_COLUMNS;
  const title = options?.title || BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_EXPORT_TITLE;
  const sheetName = options?.sheetName || BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_SHEET_NAME;
  const fileName = buildFileName(options?.fileName || BAO_CAO_BAN_HANG_NHOM_THEO_HAI_CHI_TIEU_FILE_NAME);

  const { workbook, worksheet } = createWorkbook(sheetName);

  worksheet.mergeCells(1, 1, 1, columns.length);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  if (options?.subtitle) {
    worksheet.mergeCells(2, 1, 2, columns.length);
    const subtitleCell = worksheet.getCell(2, 1);
    subtitleCell.value = options.subtitle;
    subtitleCell.font = { italic: true, size: 11 };
    subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.addRow([]);
  } else {
    worksheet.addRow([]);
  }

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  applyHeaderStyles(headerRow);

  rows.forEach(row => {
    const rowValues = columns.map(col => {
      const value = col.resolver(row);
      return value ?? '';
    });
    const excelRow = worksheet.addRow(rowValues);

    rowValues.forEach((value, colIndex) => {
      if (typeof value === 'number') {
        const cell = excelRow.getCell(colIndex + 1);
        cell.numFmt = '#,##0.00';
      }
    });

    applyDataRowStyles(excelRow, Boolean(row.is_group));
  });

  columns.forEach((col, index) => {
    worksheet.getColumn(index + 1).width = col.width || 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};

export const exportFromApi = async ({ queryParams = {}, getAll, excelOptions }: ExportFromApiOptions) => {
  const rows = await getAll?.(queryParams, 200);
  return exportReportExcel(rows, excelOptions);
};
